import { useEffect, type RefObject } from "react";
import type { Virtualizer } from "@tanstack/react-virtual";

interface UseRowFlashOptions {
  flashId: string | null | undefined;
  rows: Array<{ id: string }>;
  virtualizer: Pick<Virtualizer<Element, Element>, "scrollToIndex">;
  tableRef: RefObject<HTMLTableElement | null>;
}

/**
 * Flash + scroll-into-view a freshly-jumped-to row. Because the row may not
 * be in the DOM yet (virtualized), this asks the virtualizer to scroll to
 * its index first, then applies the flash class on the next frame. Single
 * source for the effect previously duplicated in PeopleTable and RolesTable.
 */
export function useRowFlash({ flashId, rows, virtualizer, tableRef }: UseRowFlashOptions): void {
  useEffect(() => {
    if (!flashId) return;
    const idx = rows.findIndex((r) => r.id === flashId);
    if (idx < 0) return;
    virtualizer.scrollToIndex(idx, { align: "center" });
    const raf = requestAnimationFrame(() => {
      const row = tableRef.current?.querySelector<HTMLElement>(`[data-rowid="${flashId}"]`);
      if (!row) return;
      row.classList.remove("iam-row-flash");
      void row.offsetWidth;
      row.classList.add("iam-row-flash");
    });
    return () => {
      cancelAnimationFrame(raf);
      const row = tableRef.current?.querySelector<HTMLElement>(`[data-rowid="${flashId}"]`);
      row?.classList.remove("iam-row-flash");
    };
  }, [flashId, rows, virtualizer, tableRef]);
}
